<template>
  <div class="bg-light min-vh-100">
    <main class="container">
      <div class="row justify-content-center">
        <div class="col-lg-10">
          <div class="card shadow-sm">
            <div class="card-body">
              <!-- Header: title + New Template button -->
              <div
                class="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2"
              >
                <h1 class="h4 mb-0">Templates</h1>
                <!-- New Template button visible only for supervisors/admins -->
                <button
                  v-if="isSupervisor"
                  class="btn btn-primary btn-sm"
                  @click="goCreateTemplate"
                >
                  New Template
                </button>
              </div>

              <!-- Filter row (visible only for supervisors) -->
              <div
                v-if="isSupervisor"
                class="d-flex justify-content-end mb-3"
              >
                <div class="d-flex align-items-center gap-2">
                  <label class="form-label mb-0 small">Show:</label>
                  <select
                    v-model="filterMode"
                    class="form-select form-select-sm"
                    style="max-width: 200px;"
                  >
                    <option value="active">Active templates</option>
                    <option value="inactive">Inactive templates</option>
                    <option value="all">All templates</option>
                  </select>
                </div>
              </div>

              <!-- Loading state indicator -->
              <div v-if="loading" class="text-center py-4">
                <div class="spinner-border" role="status"></div>
              </div>

              <!-- Error state display -->
              <div v-else-if="error" class="alert alert-danger py-2">
                {{ error }}
              </div>

              <!-- Empty state message -->
              <div
                v-else-if="visibleTemplates.length === 0"
                class="text-muted"
              >
                No templates found.
              </div>

              <!-- Templates table display -->
              <div v-else class="table-responsive">
                <table class="table table-sm align-middle">
                  <thead class="table-light">
                    <tr>
                      <th style="width: 25%;">Name</th>
                      <th>Description</th>
                      <th style="width: 120px;">Status</th>
                      <th style="width: 80px;"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="tmpl in visibleTemplates"
                      :key="tmpl.template_id"
                    >
                      <td>{{ tmpl.template_name }}</td>
                      <td class="text-muted">
                        {{ tmpl.template_description }}
                      </td>
                      <td>
                        <span
                          class="badge"
                          :class="tmpl.active_template ? 'bg-success' : 'bg-secondary'"
                        >
                          {{ tmpl.active_template ? "Active" : "Inactive" }}
                        </span>
                      </td>
                      <td class="text-end">
                        <button
                          class="btn btn-outline-secondary btn-sm"
                          @click="goDetail(tmpl.template_id)"
                        >
                          Open
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import { APIService } from "../http/APIService";

export default {
  name: "ViewTemplates",
  data() {
    return {
      templates: [], // Array to hold all templates fetched from API
      loading: false, // Loading state indicator
      error: null, // Error message if any occurs during fetching
      api: new APIService(), // Instance of APIService for making API calls
      currentUser: null, // Current user information
      // Filter mode for supervisors: 'active', 'inactive', or 'all'
      filterMode: "active",
    };
  },
  created() {
    this.loadCurrentUser(); // Load current user from local storage
    this.fetchTemplates(); // Fetch templates from API
  },
  computed: {
    userRole() {
      return (this.currentUser?.role || "").toLowerCase(); // Get user role in lowercase
    },
    isSupervisor() {
      // Check if the user is a supervisor or admin
      return this.userRole === "supervisor" || this.userRole === "admin";
    },
    // Filter templates based on user role and selected filter mode
    visibleTemplates() {
      const all = Array.isArray(this.templates) ? this.templates : [];

      if (!this.isSupervisor) {
        return all.filter((t) => t.active_template); // Techs see only active templates
      }

      // Supervisors/admins: respect filterMode
      if (this.filterMode === "active") {
        return all.filter((t) => t.active_template); // Show active templates
      }
      if (this.filterMode === "inactive") {
        return all.filter((t) => !t.active_template); // Show inactive templates
      }
      // 'all': return all templates
      return all;
    },
  },
  methods: {
    loadCurrentUser() {
      const raw = localStorage.getItem("currentUser"); // Get current user from local storage
      if (!raw) {
        this.currentUser = null; // No user found
        return;
      }
      try {
        this.currentUser = JSON.parse(raw); // Parse user data
      } catch {
        this.currentUser = null; // Error in parsing
      }
    },

    async fetchTemplates() {
      this.loading = true; // Set loading state
      this.error = null; // Reset error state
      try {
        const accessToken = localStorage.getItem("accessToken"); // Get access token
        const res = await this.api.getTemplates(accessToken); // Fetch templates from API
        // Keep all templates; filtering is done in visibleTemplates
        this.templates = Array.isArray(res.data) ? res.data : [];
      } catch (err) {
        console.error("Failed to load templates:", err.response?.data || err); // Log error
        this.error = "Unable to load templates."; // Set error message
      } finally {
        this.loading = false; // Reset loading state
      }
    },

    goDetail(templateId) {
      // Navigate to template detail view
      this.$router.push({
        name: "TemplateDetail",
        params: { id: templateId },
      });
    },

    goCreateTemplate() {
      // Navigate to create template view
      this.$router.push({ name: "CreateTemplate" });
    },
  },
};
</script>
